import { ReactNode, createContext, useContext } from "react";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { useCompanyMetadata } from "src/context/Company/Metadata/useCompanyMetadata";
import { useUrlToApi } from "src/api/_common/params/useUrlToApi";
import { useDelayedSearch } from "src/utils/useDelayedSearch";
import { useAutoSyncGetAllProducts } from "src/api/client-api/products/getAll/useAutoSyncGetAllProducts";
import { useIntermediateProductColumns } from "./useIntermediateProductColumns";
import { useGridColumns } from "src/components/Grid/hooks/columnsExtension/useGridColumns";
import { useIntermediateProductMenuItems } from "./ProductGrid/useIntermediateProductMenuItems";
import { ProductListContextInitialValues } from "./ProductListContext";
import { isBrandCollectionError, useGetAllBrands } from "src/api/client-api/brand/useGetAllBrand";
import { useListExportAction } from "src/components/Grid/headers/useListExportAction";
import { useScoreRecalculationAction } from "src/components/Grid/headers/useScoreRecalculationAction";
import { productGridKey } from "src/api/client-api/products/useGetAllproduct";

const IntermediateProductListContext = createContext<ProductListContextInitialValues | undefined>(undefined);

type IntermediateProductListContextProviderProps = { children: ReactNode };
export const IntermediateProductListContextProvider = (props: IntermediateProductListContextProviderProps) => {
    const { children } = props;
    const gridKey = "intermediate-product-grid" as const;

    const { canUser, selectedCompany, currentCampaign } = useFoodpilotContext();
    const { company } = useCompanyMetadata();

    const { allAsAxiosParams, filters, sort, pagination } = useUrlToApi({ page: "1", itemsPerPage: "20" });
    const globalSearch = useDelayedSearch();
    const { products, isRefetching, appSearch, totalItems, isReady } = useAutoSyncGetAllProducts({
        companyId: selectedCompany?.id,
        campaignId: currentCampaign?.id,
        params: {
            ...allAsAxiosParams,
            q: globalSearch.delayedSearch === "" ? undefined : globalSearch.delayedSearch,
            isIntermediate: true,
            scoresQuery: {
                scores: {
                    subscores: false,
                },
            },
        },
    });

    const brandFilterQuery = useGetAllBrands({
        itemsPerPage: 1000,
        initSearch: "",
    });
    const brandExistenceQuery = useGetAllBrands({ itemsPerPage: 1 });
    const brandCollectionState = getBrandAuxiliaryQueryState(
        brandFilterQuery.brandCollectionState,
        brandExistenceQuery.brandCollectionState,
    );
    const refetchBrands = async () => {
        await Promise.all([brandFilterQuery.refetch(), brandExistenceQuery.refetch()]);
    };

    const mrt_columns = useIntermediateProductColumns({
        brandExistenceQuery,
        brandFilterQuery,
        data: products ?? [],
        gridKey,
    });
    const gridConfiguration = useGridColumns(mrt_columns, gridKey);

    const menuItems = useIntermediateProductMenuItems({
        canUser,
        companyIds: {
            "@id": company["@id"],
            id: company.id,
        },
    });

    const exportAction = useListExportAction({
        path: "/api/products",
        filters,
        sort,
        search: globalSearch.delayedSearch,
        campaignId: currentCampaign?.id,
        organizationId: selectedCompany?.id,
        extra: { isIntermediate: true },
        fileNameStem: "intermediate-products",
        isDisabled: currentCampaign === null,
    });

    const scoreRecalculationAction = useScoreRecalculationAction({
        path: "/api/products",
        entityKind: "product",
        invalidatedQueryKey: productGridKey,
        pageEntities: (products ?? []).map(({ id, title }) => ({ id, name: title })),
        campaignId: currentCampaign?.id,
        organizationId: selectedCompany?.id,
    });

    const items: ProductListContextInitialValues = {
        appSearch,
        brandCollectionState,
        gridConfiguration,
        gridKey: gridKey,
        isRefetching,
        isReady,
        menuItems,
        tableActions: [exportAction, scoreRecalculationAction],
        mrt_columns,
        products: products ?? [],
        totalItems,
        globalSearch,
        pagination,
        refetchBrands,
    };

    return <IntermediateProductListContext.Provider value={items}>{children}</IntermediateProductListContext.Provider>;
};

export const useIntermediateProductListContext = () =>
    useContext(IntermediateProductListContext) as ProductListContextInitialValues;

const getBrandAuxiliaryQueryState = (
    filterState: ProductListContextInitialValues["brandCollectionState"],
    existenceState: ProductListContextInitialValues["brandCollectionState"],
) => {
    const errorState = [filterState, existenceState].find(isBrandCollectionError);

    return errorState ?? filterState;
};
